import React, { FC, useEffect, useState } from 'react';
import Sheet from '@mui/joy/Sheet';
import Skeleton from 'react-loading-skeleton';
import CssBaseline from '@mui/joy/CssBaseline';
import IconButton from '@mui/material/IconButton';
import TotalPurchaseLine from './TotalPurchaseLine';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import TextAreaCustom from '../../../../../../common/TextAreaCustom';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { ModeTypes } from './AddPurchaseOrder';
import { FieldErrors } from '../../../../../../domain/helpers/Errors';
import { darkModeBackgroud } from '../../../../../../utils/constants';
import { handleFocus } from '../../../../../../common/GlobalFunctions';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { RowData } from '../../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrderLine';
import { PurchaseOrderInterface } from '../../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrder';

export interface AddProductProps {
    mode: ModeTypes;
    loading: boolean;
    isApproved: boolean;
    form: PurchaseOrderInterface;
    isRequestForQuotation: boolean;
    errors: FieldErrors | undefined;
    setForm: React.Dispatch<React.SetStateAction<PurchaseOrderInterface>>;
}

const AddProduct: FC<AddProductProps> = ({ mode, form, isApproved, isRequestForQuotation, errors, loading, setForm }) => {
    const isView = mode === 'View';

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const [rows, setRows] = useState<RowData[]>([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

    useEffect(() => {
        if (form) {
            const updatedRows = form.purchaseOrderLines.map(item => ({
                ...item,
                total: item.quantity * item.price,
            }));

            setRows(updatedRows);
        }
        else setRows([]);

    }, [form]);

    useEffect(() => {
        const allRowsFilled = rows.every(row => row.title.length > 1 && row.quantity > 0 && row.price > 0);
        setIsButtonDisabled(!allRowsFilled);
    }, [rows]);

    const handleAddRow = () => {
        delete errors?.['purchaseOrderLines'];
        const newRow: RowData = {
            id: `New-Row-${Math.random().toString()}`,
            title: '',
            quantity: 1,
            price: 1,
            total: 1,
        };

        const purchaseOrderLines = [...rows, newRow];

        setRows(purchaseOrderLines);
        setForm({ ...form, purchaseOrderLines })
    };

    const handleRemoveRow = (id: string) => {
        const purchaseOrderLines = rows.filter(row => row.id !== id);
        setRows(purchaseOrderLines);
        setForm({ ...form, purchaseOrderLines });
    }

    const handleInputChange = (id: string, field: keyof RowData, value: string | number) => {
        delete errors?.['purchaseOrderLines'];
        const updatedRows = rows.map(row => {
            if (row.id === id) {
                const updatedRow = { ...row, [field]: value };
                const quantity = field === 'quantity' ? Number(value) : Number(row.quantity);
                const price = field === 'price' ? Number(value) : Number(row.price);
                const total = (quantity * price).toFixed(2);
                updatedRow.total = Number(total);
                return updatedRow;
            }
            return row;
        });

        const purchaseOrderLines = updatedRows.map(row => {
            const { total, ...rest } = row;
            return rest;
        });

        setForm({ ...form, purchaseOrderLines });
        setRows(updatedRows);
    };

    const totalPurchaseOrderLines = Number(rows?.reduce((sum, row) => sum + (row.total || 0), 0).toFixed(2));

    const isDiabled = isView || isApproved || isRequestForQuotation;

    return (
        <div className='w-100 d-flex justify-content-center'>
            <CssBaseline />
            <Sheet
                sx={{
                    width: '100%',
                    my: 0,
                    py: 4,
                    px: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    borderRadius: 'md',
                    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
                    background: darkMode ? 'var(--dark-mode-bg)' : 'white'
                }}
            >
                <div className='mb-0 d-flex flex-row align-items-center' style={{ color: darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)', fontSize: 20 }}>
                    {language['Products']}
                    {!isDiabled
                        && <IconButton
                            aria-label="edit"
                            title='Add Product'
                            sx={{ color: "var(--color-pink)", marginLeft: 'auto', marginBottom: 0.5 }}
                            onClick={handleAddRow}
                            disabled={isButtonDisabled}
                        >
                            <AddOutlinedIcon />
                        </IconButton>}
                </div>
                <div className='mt-4'>
                    <table className={`table ${darkMode && 'table-dark'}`}>
                        <thead>
                            <tr>
                                <th style={{ width: '45%' }}>{language['Product/Service']}</th>
                                <th>{language['Quantity']}</th>
                                <th>{language['Price']}</th>
                                <th style={{ width: 200 }}>{language['Total']}</th>
                                {!isDiabled && <th />}
                            </tr>
                        </thead>
                        {
                            loading
                                ? <tbody>
                                    {
                                        [1, 2].map(item =>
                                            <tr key={`tr-skeleton-${item}`}>
                                                {[1, 2, 3, 4, 5, 6].map(i => <td key={`td-skeleton-item-${i}`}><Skeleton className="mt-4" style={{ width: '100%', height: 41 }} /></td>)}
                                            </tr>
                                        )
                                    }
                                </tbody>
                                : <tbody>
                                    {
                                        rows?.map((row) => (
                                            <tr key={row.id}>
                                                <td>
                                                    <input
                                                        value={row.title}
                                                        readOnly={isDiabled}
                                                        className={`form form-control ${errors?.['purchaseOrderLines'] === `"Title" is not allowed to be empty` && 'border border-danger'}`}
                                                        style={{ background: darkMode ? darkModeBackgroud : 'initial', color: darkMode ? 'white' : 'initial' }}
                                                        onChange={(e) => handleInputChange(row.id, 'title', e.target.value)}
                                                    />
                                                    {
                                                        errors?.['purchaseOrderLines'] === `"Title" is not allowed to be empty`
                                                        && <div className='invalid-feedback-danger'>
                                                            {errors?.['purchaseOrderLines']}
                                                        </div>
                                                    }
                                                </td>
                                                <td>
                                                    <input
                                                        value={row.quantity}
                                                        readOnly={isDiabled}
                                                        className={`form form-control ${errors?.['purchaseOrderLines'] === `"Quantity" must be greater than or equal to 0.001` && 'border border-danger'}`}
                                                        style={{ background: darkMode ? darkModeBackgroud : 'initial', color: darkMode ? 'white' : 'initial' }}
                                                        onChange={(e) => handleInputChange(row.id, 'quantity', parseFloat(e.target.value) || 0)}
                                                        type="number"
                                                        onFocus={handleFocus}
                                                        onBlur={handleFocus}
                                                    />
                                                    {
                                                        errors?.['purchaseOrderLines'] === `"Quantity" must be greater than or equal to 0.001`
                                                        && <div className='invalid-feedback-danger'>
                                                            {errors?.['purchaseOrderLines']}
                                                        </div>
                                                    }
                                                </td>
                                                <td>
                                                    <div
                                                        className={`input-group ${errors?.['purchaseOrderLines'] === `"Price" must be greater than or equal to 0.001` && 'border border-danger'}`}
                                                        style={{ borderRadius: 6 }}
                                                    >
                                                        <input
                                                            value={row.price}
                                                            readOnly={isDiabled}
                                                            className='form form-control'
                                                            style={{
                                                                background: darkMode ? darkModeBackgroud : 'initial',
                                                                color: darkMode ? 'white' : 'initial',
                                                            }}
                                                            type="number"
                                                            onChange={(e) => handleInputChange(row.id, 'price', parseFloat(e.target.value) || 0)}
                                                            onFocus={handleFocus}
                                                            onBlur={handleFocus}
                                                        />
                                                        <span className="input-group-text">€</span>
                                                    </div>
                                                    {
                                                        errors?.['purchaseOrderLines'] === `"Price" must be greater than or equal to 0.001`
                                                        && <div className='invalid-feedback-danger'>
                                                            {errors?.['purchaseOrderLines']}
                                                        </div>
                                                    }
                                                </td>
                                                <td>
                                                    <div className="input-group" style={{ width: 200 }}>
                                                        <input
                                                            value={row.total.toFixed(2)}
                                                            type="number"
                                                            readOnly
                                                            className='form form-control'
                                                            style={{ background: darkMode ? darkModeBackgroud : 'initial', color: darkMode ? 'white' : 'initial' }}
                                                            onChange={(e) => handleInputChange(row.id, 'total', parseFloat(e.target.value) || 0)}
                                                        />
                                                        <span className="input-group-text">€</span>
                                                    </div>
                                                </td>
                                                {!isDiabled
                                                    && <td>
                                                        <IconButton
                                                            aria-label="edit"
                                                            title='Delete Product'
                                                            color='error'
                                                            sx={{ marginLeft: 'auto', marginBottom: 0.5 }}
                                                            onClick={() => handleRemoveRow(row.id)}
                                                        >
                                                            <DeleteOutlinedIcon />
                                                        </IconButton>
                                                    </td>}
                                            </tr>
                                        ))}
                                    {
                                        errors?.['purchaseOrderLines']
                                        && errors?.['purchaseOrderLines'] === `"Purchase Order Lines" must contain at least 1 items`
                                        && <tr>
                                            <td colSpan={5}>
                                                <div className='invalid-feedback-danger'>
                                                    {errors?.['purchaseOrderLines']}
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                    <tr key='Description&Total'>
                                        <td colSpan={4}>
                                            <div className='d-flex flex-row gap-2 align-items-center'>
                                                <div style={{ width: '82%' }}>
                                                    <label
                                                        title={language['Description']}
                                                        id={'Description'}
                                                        htmlFor={'Description'}
                                                        className={`${darkMode ? 'text-white' : 'text-dark'}`}
                                                    >
                                                        {language['Description']}
                                                    </label>
                                                    <TextAreaCustom
                                                        errors={errors ?? {}}
                                                        form={form}
                                                        name='description'
                                                        setForm={setForm}
                                                        view={isDiabled}
                                                    />
                                                    {
                                                        errors?.['description']
                                                        && <div className='invalid-feedback-danger'>
                                                            {language['Description']} {language['IsRequired']}
                                                        </div>
                                                    }
                                                </div>
                                                <TotalPurchaseLine
                                                    disabled={isDiabled}
                                                    form={form}
                                                    setForm={setForm}
                                                    totalPurchaseOrderLines={totalPurchaseOrderLines}
                                                />
                                            </div>
                                        </td>
                                        <td />
                                    </tr>
                                </tbody>
                        }
                    </table>
                </div>
            </Sheet>
        </div>
    );
};

export default AddProduct;
